import { LiquidityAmount, PercentAmount, TokenAmount } from '@akropolis-web/primitives';
import React from 'react';

import { FormattedAmount } from 'components';
import { colors, styled } from 'core/styles';
import { MaybeEither, combine, toEither } from 'utils/either';
import { DecreaseIcon, IncreaseIcon } from 'components/icons';
import { calculateGrowth } from 'domain/utils';

type MetricWidgetProps<A extends LiquidityAmount | TokenAmount | PercentAmount> = {
  title: React.ReactNode;
  amount: MaybeEither<A>;
  prevAmount: MaybeEither<A | null | undefined>;
  displayGrowthAs: 'percent' | 'usd';
  formatPrecision?: number;
  growthTime?: '24H' | '7D';
};

export function MetricWidget<A extends LiquidityAmount | TokenAmount | PercentAmount>({
  title,
  amount: meAmount,
  prevAmount: mePrevAmount,
  displayGrowthAs,
  formatPrecision,
  growthTime = '24H',
}: MetricWidgetProps<A>) {
  const growthAmount = combine(toEither(meAmount), toEither(mePrevAmount))
    .map(([amount, prevAmount]) => {
      const growth = calculateGrowth(amount, prevAmount);
      return displayGrowthAs === 'percent' ? growth?.percent : growth?.amount;
    })
    .toUndefined();

  return (
    <MetricContainer>
      <MetricTitle>{title}</MetricTitle>

      {growthAmount && (
        <GrowthContainer>
          <GrowthBadge variant={growthAmount.gte(0) ? 'increase' : 'decrease'}>
            {growthAmount.gte(0) ? <IncreaseIcon /> : <DecreaseIcon />}
            <FormattedAmount
              sum={growthAmount.withValue(growthAmount.abs())}
              precision={2}
              symbolSize="inherit"
              withSI
            />
          </GrowthBadge>
          <GrowthTime>{growthTime === '24H' ? '24H' : '7D'}</GrowthTime>
        </GrowthContainer>
      )}
      <MetricAmount>
        <FormattedAmount sum={meAmount} precision={formatPrecision} symbolSize="inherit" withSI />
      </MetricAmount>
    </MetricContainer>
  );
}

const MetricContainer = styled('div')(() => ({
  minHeight: 152,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  fontSize: 12,
  lineHeight: '16px',
}));

const MetricTitle = styled('div')(() => ({
  letterSpacing: '0.25px',
  paddingBottom: 24,
  marginBottom: 'auto',
}));

const MetricAmount = styled('div')(({ theme }) => ({
  fontFamily: theme.typography.secondaryFontFamily,
  fontSize: 32,
  lineHeight: '32px',
}));

const GrowthContainer = styled('div')(({ theme }) => ({
  fontFamily: theme.typography.secondaryFontFamily,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

const GrowthBadge = styled('div')<{ variant: 'increase' | 'decrease' }>(({ variant }) => ({
  height: 28,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  color: variant === 'increase' ? colors.greenShamrock : colors.scarlet[100],
  background: variant === 'increase' ? colors.springGreen[12] : colors.scarlet[16],
  padding: '4px 8px 4px 4px;',
  borderRadius: 4,
}));

const GrowthTime = styled('div')(() => ({
  height: 28,
  display: 'flex',
  alignItems: 'center',
  color: colors.blackOpacity[40],
  background: colors.blackOpacity[4],
  padding: '4px 8px',
  borderRadius: 4,
}));
